/**
 * Residents with no building (2026-10-13; any origin with a CounselEAR clinic since 2026-10-14), no confirmed
 * building yet. Mirrors GET /api/v1/manifest-intake/residents/no-building
 * (services/counselear/residentCreate.js `unplaced`).
 *
 * Its own file on purpose: manifestApi.ts is another builder's surface.
 */
import { apiRequest } from '../../services/api';

const BASE = '/manifest-intake';

export type BuildingRef = { id: string; name: string | null };

export type NoBuildingResident = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  counselear_id: string | null;
  created_at: string;
  /** What created the resident: COUNSELEAR, MONDAY or PATIENT_ADVOCATE. */
  creation_origin?: string | null;
  twl: boolean;
  open_case: boolean;
  /** A building one manifest listed them at: proposed, not confirmed. */
  proposed_facility: BuildingRef | null;
  /** Their CounselEAR Preferred Clinic. */
  clinic_id: string | null;
  /** The clinic's name in CounselEAR (20261014a); null until eMerge has read it. */
  clinic_name?: string | null;
  /** The building that clinic was ratified as since they were created: a person places them. */
  clinic_facility: BuildingRef | null;
  /**
   * A provider added them from CounselEAR while working this building (2026-10-28, docs/provider/CE_PULL.md): they
   * say the resident is there. `by` is a first name and last initial. Absent before the deploy.
   */
  provider_added?: { facility: BuildingRef; at: string | null; by: string | null } | null;
};

/**
 * A provider's statement that disagrees with where eMerge has the resident (GET /residents/provider-statements):
 * CLINIC_ELSEWHERE -- refused, the person's CounselEAR clinic is confirmed as a building the provider does not cover
 * (no resident was created); CREATED / LINKED_EXISTING -- the resident lives at another building than the provider's.
 */
export type ProviderStatement = {
  id: string;
  at: string;
  outcome: 'CLINIC_ELSEWHERE' | 'CREATED' | 'LINKED_EXISTING';
  counselear_id: string | null;
  by: string | null;
  provider_building: BuildingRef;
  clinic: { id: string; name: string | null } | null;
  clinic_building: BuildingRef | null;
  resident: { id: string; first_name: string | null; last_name: string | null; building: BuildingRef | null } | null;
};
export type ProviderStatements = { enterprise_only: boolean; ready: boolean; items: ProviderStatement[] };

export type NoBuildingList = {
  /** A facility-scoped reviewer may not see residents who belong to no facility. */
  enterprise_only: boolean;
  total: number | null;
  page?: number;
  limit: number;
  offset: number;
  items: NoBuildingResident[];
};

/** One CounselEAR clinic with residents who have no building (GET /clinics/no-building). Counts and names of places only. */
export type ClinicGroup = {
  /** null: the residents CounselEAR named no clinic for. */
  clinic_id: string | null;
  clinic_name: string | null;
  enabled: boolean | null;
  /** Residents with no confirmed building (the No building list), whatever created them. */
  residents: number;
  /** Of them, those a confirmation places (no building recorded at all). Absent from an older server: all of them. */
  placeable?: number;
  /** How many of them Monday created (placed too: ruling, Isaiah Cotto, 2026-10-14). */
  from_monday?: number;
  /** Where the clinic's residents who have a building live: the commonest building and its share. */
  suggestion: { facility_id: string; facility_name: string | null; at_building: number; placed: number; share: number | null; buildings: number; basis: string } | null;
  /** Already confirmed (its residents are waiting to be placed again). */
  ratified: { decision_id: string; facility_id: string; facility_name: string | null } | null;
  /** A standing proposal from residents' history, not yet confirmed. */
  proposed: { decision_id: string; facility_id: string; facility_name: string | null } | null;
};

export type ClinicGroups = { enterprise_only: boolean; can_confirm: boolean; items: ClinicGroup[] };
export type FacilityHit = { id: string; name: string; city: string | null; state: string | null };
export type ClinicPlacement = {
  ratified?: boolean; facility_id?: string | null; qualifying?: number; placed?: number; already?: number; skipped?: number;
  failed?: number; human_decision?: number; by_origin?: Record<string, number>; authority_refused?: boolean; error?: string;
};
export type ConfirmResult = { decision_id: string | null; ratified: boolean; already: boolean; placement: ClinicPlacement | null };

export const noBuildingApi = {
  list: (page = 1, limit = 100) =>
    apiRequest<NoBuildingList>('GET', `${BASE}/residents/no-building?page=${page}&limit=${limit}`),
  clinics: () => apiRequest<ClinicGroups>('GET', `${BASE}/clinics/no-building`),
  /** What providers said that disagrees with a resident's building (2026-10-28, docs/provider/CE_PULL.md). */
  statements: () => apiRequest<ProviderStatements>('GET', `${BASE}/residents/provider-statements`),
  facilities: (q: string) => apiRequest<{ items: FacilityHit[] }>('GET', `${BASE}/clinics/facilities?q=${encodeURIComponent(q)}`),
  /** "Set this clinic's building": confirm, and place its residents with no building, whatever created them. */
  // `expectedRatifiedId`: the clinic's ratified decision this screen saw, or null for none. The server compares it
  // under the clinic's lock and answers 409 STALE if someone confirmed the clinic since. `replace`: only after the
  // person was told they are changing a confirmed building (409 RATIFIED_ELSEWHERE otherwise).
  setBuilding: (clinicId: string, facilityId: string, { expectedProposalId = null, expectedRatifiedId, replace = false }:
    { expectedProposalId?: string | null; expectedRatifiedId: string | null; replace?: boolean }) =>
    apiRequest<ConfirmResult>('POST', `${BASE}/clinics/${encodeURIComponent(clinicId)}/building`, {
      facility_id: facilityId, expected_proposal_id: expectedProposalId, expected_ratified_decision_id: expectedRatifiedId, replace,
    }),
};
